import { observable, action, computed, runInAction, autorun, reaction } from 'mobx'
import Auth0Lock from 'auth0-lock'
import decodeJwt from 'jwt-decode'
import moment from 'moment'
import analytics from 'misc/analytics'
import { Store } from 'libx'
import CurrentUser from 'domain/CurrentUser'
import env from 'misc/env'

const _lock = new Auth0Lock(
  env.AUTH0_CLIENT_ID,
  env.AUTH0_DOMAIN,
  {
    theme: {
      logo: require('assets/img/taxfyle-icon-256px.png'),
      primaryColor: '#A776E5'
    },
    languageDictionary: {
      title: 'Taxfyle'
    },
    auth: {
      sso: true,
      redirect: false,
      responseType: 'token',
      connections: ['taxfyle-com'],
      authParams: {
        scope: 'openid email user_metadata app_metadata picture'
      }
    }
  }
)

export default class AuthStore extends Store {
  @observable user = null
  @observable token = null
  @observable secondsUntilTokenExpires = 9999

  constructor ({ storage }) {
    super(...arguments)
    runInAction('load token', () => {
      this.token = storage.getItem('jwt')
      if (this.tokenExpires.isBefore(moment())) {
        this.token = null
      }
    })
    setInterval(action(() => {
      // Makes sure these are updated every second.
      if (!this.token) {
        return
      }
      const from = moment()
      const to = this.tokenExpires.clone()
      const diff = to.clone().startOf('second').diff(from.clone().startOf('second'))
      if (this.secondsUntilTokenExpires < 1) {
        this.logout()
        return
      }
      this.secondsUntilTokenExpires = moment.duration(diff).asSeconds()
    }), 1000)
    // Persist the token to storage whenever it changes.
    autorun(() => {
      if (!this.token) {
        storage.removeItem('jwt')
      } else {
        storage.setItem('jwt', this.token)
      }
    })
  }

  /**
   * Decoded version of the token.
   */
  @computed get decodedToken () {
    if (this.token) {
      return decodeJwt(this.token)
    }

    return null
  }

  @computed get tokenExpires () {
    const decoded = this.decodedToken
    if (!decoded) return moment()
    return moment.unix(decoded.exp)
  }

  @computed get shouldShowReloadWarning () {
    return this.secondsUntilTokenExpires <= 120
  }

  @action login () {
    return this._showDialog({ initialScreen: 'login' })
  }

  @action signup ({ hideLogin } = {}) {
    return this._showDialog({ initialScreen: 'signUp', allowLogin: !hideLogin })
  }

  @action setToken (token) {
    this.token = token
  }

  @action setReferrerMeta (data) {
    this.referrerParams = data
  }

  logout () {
    runInAction('logout', () => {
      this.token = null
    })
    location.reload()
  }

  @action _setUser (attributes) {
    if (process.env.NODE_ENV !== 'production') {
      console.log('Token:', this.token)
      console.log('User:', attributes)
    } else {
      window.__token = this.token
    }

    if (this.user) {
      this.user.set(attributes, { parse: true, stripUndefined: true })
    } else {
      let user = this.rootStore.userStore.users.get(attributes.user_id)
      if (!user) {
        user = new CurrentUser(attributes, { rootStore: this.rootStore, stripUndefined: true, parse: true })
        this.rootStore.userStore.users.add(user)
      }

      this.user = user
    }
    window.Intercom('boot', {
      app_id: env.INTERCOM_APP_ID,
      email: this.user.email,
      name: this.user.displayName,
      user_id: this.user.id,
      custom_launcher_selector: '#intercom-launcher'
    })
    analytics.identify(this.user.id, {
      name: this.user.name,
      email: this.user.displayName
    })
  }

  /**
   * Called by the router to check if we are still authenticated.
   */
  @action async checkAuthentication () {
    try {
      const expires = this.tokenExpires
      const now = moment()
      if (now.add(2, 'minutes').isAfter(expires)) {
        return false
      }

      if (!this.user) {
        const user = await _fetchProfile(this.token)
        this._setUser(user)
      }

      return true
    } catch (err) {
      console.error('Error checking authentication:', err)
      return false
    }
  }

  onLogout (fn) {
    reaction(
      () => this.token,
      (token) => {
        if (!token) {
          fn()
        }
      }
    )
    return this
  }

  onLogin (fn) {
    if (this.user) {
      fn()
    }

    reaction(
      () => this.user,
      (user) => {
        if (user) {
          fn()
        }
      }
    )

    return this
  }

  async _showDialog (opts) {
    const result = await _login(opts)
    if (result === false) {
      return false
    }

    const { profile, token } = result

    runInAction(() => {
      this.token = token
      this._setUser(profile)
    })

    if (this.referrerParams && Object.keys(this.referrerParams).length > 0) {
      const { referrer, ...referrerMeta } = this.referrerParams
      await this.rootStore.api.account.updateMeta({
        referrer,
        referrerMeta
      })
    }

    return true
  }
}

async function _login (opts) {
  const result = await new Promise((resolve, reject) => {
    const errHandler = (err) => {
      reject(err)
    }

    _lock.once('authenticated', (result) => {
      const { idToken: jwt } = result
      _lock.getProfile(jwt, (err, profile) => {
        if (err) {
          reject(err)
          return
        }

        // Auto-hide the lock.
        setTimeout(() => _lock.hide(), 1100)
        resolve({ profile, token: jwt })
      })
    })

    _lock.once('authorization_error', errHandler)
    _lock.once('unrecoverable_error', errHandler)
    _lock.once('hide', () => resolve(false))
    _lock.show(opts)
  })

  if (!result) {
    return false
  }

  return result
}

async function _fetchProfile (token) {
  return new Promise((resolve, reject) => {
    _lock.getProfile(
      token,
      (err, profile) => err
        ? reject(err)
        : resolve(profile)
    )
  })
}



// WEBPACK FOOTER //
// ./src/client/store/AuthStore.js